import { useState, useRef, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { MenuOutlined, UserOutlined, DownOutlined } from '@ant-design/icons'
import { useAuthorization } from '../contexts/AuthorizationContext'
import { buildProfileMenu, resolveHomePath } from '../config/nav'
import { useAuthSession } from '../lib/session'

type TopBarProps = {
  sidebarCollapsed: boolean
  onMenuClick: () => void
}

export default function TopBar({ sidebarCollapsed, onMenuClick }: TopBarProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const session = useAuthSession()
  const { permissions } = useAuthorization()
  const profileLinks = useMemo(() => buildProfileMenu(session, permissions), [session, permissions])

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const showLogoAndMenu = sidebarCollapsed
  const displayName = session?.currentUsername || session?.email || 'User'
  const homePath = resolveHomePath(session, permissions)

  return (
    <header className="h-14 lg:h-16 px-4 lg:px-6 flex items-center justify-between bg-white border-b-2 border-brand-200/80">
      <div className="flex items-center gap-3 min-w-0">
        {showLogoAndMenu && (
          <>
            <button
              type="button"
              onClick={onMenuClick}
              className="p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-brand-50 focus:outline-none focus:ring-2 focus:ring-brand-500/30 transition-colors shrink-0"
              aria-label="Menu"
            >
              <MenuOutlined className="w-5 h-5" />
            </button>
            <Link to={homePath} className="flex items-center gap-2.5 min-w-0 shrink-0 group">
              <img
                src="/assets/auth/logo-vividteal.svg"
                alt="Dashboard Insights"
                className="h-8 w-auto shrink-0"
              />
            </Link>
          </>
        )}
      </div>
      <div className="relative shrink-0" ref={ref}>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="flex items-center gap-2 lg:gap-3 px-2 py-1.5 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <span className="text-sm font-semibold text-gray-800 hidden sm:inline font-display">{displayName}</span>
          <div className="w-8 h-8 lg:w-9 lg:h-9 rounded-full border-2 border-brand-500 flex items-center justify-center bg-white shadow-card">
            <UserOutlined className="w-4 h-4 lg:w-5 lg:h-5 text-brand-600" />
          </div>
          <DownOutlined className="w-4 h-4 text-gray-500" />
        </button>
        {open && (
          <div className="absolute right-0 top-full mt-1 py-1 w-48 bg-white rounded-xl shadow-card-hover border border-gray-200 z-50">
            {profileLinks.map((item) => (
              <Link
                key={item.key}
                to={item.path}
                className="block px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-brand-50 hover:text-brand-700"
                onClick={() => setOpen(false)}
              >
                {item.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    </header>
  )
}
